import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useSubscription } from '@truepill/tpos-react-router'
import { SPLIT_FLIGHT_TIME_LIMIT } from '@truepill/tpos-types'
import { GET_SPLIT_PROGRESS, SUBSCRIBE_SPLIT_PROGRESS } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { goToFulfillmentOrder } from 'routes'
import type { ChildProps } from 'types'
import type { SplitOutOrderRef, SplitProgressOrder } from 'utils/splitProgress'
import { splitCompleteMessage, splitGaveUpMessage, splitProgressFor } from 'utils/splitProgress'

const linkTo = ({ orderId }: SplitOutOrderRef) => ({
  label: 'Click here to open new order',
  to: goToFulfillmentOrder({ orderId }),
})

// Live updates can be lost on the way to the tab, so a split in flight is also fetched this often
export const SPLIT_PROGRESS_POLL_INTERVAL = 5000

// A split this tab requested: the original order and the split entry's fill request token
export type WatchedSplit = { orderId: string; coreFillRequestToken: string }

type SplitWatchContextProps = { watchSplit: (split: WatchedSplit) => void }

const SplitWatchContext = createContext<SplitWatchContextProps>({ watchSplit: () => undefined })

export const useSplitWatch = (): SplitWatchContextProps => useContext(SplitWatchContext)

// Tells the requester how each split they started in this tab ended, wherever they have navigated.
// In memory only: after a reload the order page shows the state.
const SplitWatchProvider = ({ children }: ChildProps): JSX.Element => {
  const client = useApolloClient()
  const { locationsById } = useTPCacheContext()
  const showSuccessToast = useSuccessToast(true)
  const showErrorToast = useErrorToast()
  // The ref is the source of truth, so events arriving back to back for one commit toast once
  const watchedRef = useRef(new Map<string, WatchedSplit>())
  const staleTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const [orderIds, setOrderIds] = useState<string[]>([])

  const syncOrderIds = useCallback(() => {
    setOrderIds([...new Set([...watchedRef.current.values()].map(split => split.orderId))].sort())
  }, [])

  const stopWatching = useCallback(
    (coreFillRequestToken: string) => {
      watchedRef.current.delete(coreFillRequestToken)
      clearTimeout(staleTimersRef.current.get(coreFillRequestToken))
      staleTimersRef.current.delete(coreFillRequestToken)
      syncOrderIds()
    },
    [syncOrderIds],
  )

  const watchSplit = useCallback(
    (split: WatchedSplit) => {
      if (watchedRef.current.has(split.coreFillRequestToken)) return
      watchedRef.current.set(split.coreFillRequestToken, split)
      // Once the split goes stale the chip says it didn't complete; a later arrival is left to the order page
      staleTimersRef.current.set(
        split.coreFillRequestToken,
        setTimeout(() => stopWatching(split.coreFillRequestToken), SPLIT_FLIGHT_TIME_LIMIT),
      )
      syncOrderIds()
    },
    [stopWatching, syncOrderIds],
  )

  useEffect(() => {
    const staleTimers = staleTimersRef.current
    return () => staleTimers.forEach(clearTimeout)
  }, [])

  // Toasts each watched split the order shows as ended; true when it toasted any
  const handleOrder = (order?: SplitProgressOrder | null): boolean => {
    if (!order) return false
    let toasted = false
    const splits = [...watchedRef.current.values()].filter(split => split.orderId === order._id)
    for (const split of splits) {
      const progress = splitProgressFor(order, split.coreFillRequestToken)
      if (progress.kind === 'wait') continue
      stopWatching(split.coreFillRequestToken)
      if (progress.kind === 'drop') continue

      toasted = true
      const { splitOutOrder } = progress
      // The split-out order's own location, which core compliance may have changed from the target
      const locationName = splitOutOrder.locationId ? locationsById[splitOutOrder.locationId]?.name : undefined
      if (progress.kind === 'complete') {
        showSuccessToast(splitCompleteMessage(order.coreOrderToken, progress, locationName), {
          persistent: true,
          link: linkTo(splitOutOrder),
        })
      } else {
        showErrorToast(splitGaveUpMessage(order.coreOrderToken, progress, locationName), {
          persistent: true,
          link: linkTo(splitOutOrder),
        })
      }
    }
    return toasted
  }

  // The poll reads the latest handler, so a render doesn't restart its timer
  const handleOrderRef = useRef(handleOrder)
  useEffect(() => {
    handleOrderRef.current = handleOrder
  })

  // no-cache, here and in the poll: this selection is narrower than the order page's, so writing it to
  // the cache would overwrite nested objects the page relies on; the page keeps its own subscription
  useSubscription<{ orderUpdate?: { order?: SplitProgressOrder } }>(SUBSCRIBE_SPLIT_PROGRESS, {
    skip: !orderIds.length,
    variables: { orderIds },
    fetchPolicy: 'no-cache',
    onData: ({ data }) => handleOrder(data.data?.orderUpdate?.order),
  })

  useEffect(() => {
    if (!orderIds.length) return
    const poll = setInterval(() => {
      for (const orderId of orderIds) {
        client
          .query<{ getOrder?: SplitProgressOrder }>({
            query: GET_SPLIT_PROGRESS,
            variables: { orderId },
            fetchPolicy: 'no-cache',
          })
          .then(({ data }) => {
            // The poll got there first, so the live update was lost or is late, and the order page's
            // subscription on this tab is in the same state
            if (handleOrderRef.current(data?.getOrder)) void client.refetchQueries({ include: ['getFullOrder'] })
          })
          // A failed poll is retried on the next tick
          .catch(() => undefined)
      }
    }, SPLIT_PROGRESS_POLL_INTERVAL)
    return () => clearInterval(poll)
  }, [client, orderIds])

  const value = useMemo(() => ({ watchSplit }), [watchSplit])

  return <SplitWatchContext.Provider value={value}>{children}</SplitWatchContext.Provider>
}

export default SplitWatchProvider
